import * as turf from '@turf/turf';
import { roundToDecimals } from './numbers';
import { metersToFt } from './units';
export const getPolyArea = (coordinates) => {
    const polygon = turf.polygon([coordinates]);
    return getGeojsonAreaFt(polygon);
};
export const getGeojsonAreaFt = (geometry) => {
    const areaInMeters = turf.area(geometry);
    return metersToFt(areaInMeters);
};
const toMultiPolygon = (geometry) => geometry.type === 'Polygon'
    ? turf.multiPolygon([geometry.coordinates]).geometry
    : geometry;
export const getCoordinatesWithPadding = (coordinates, bufferVal, units) => {
    const polygon = turf.polygon([coordinates]);
    const buffer = turf.buffer(polygon, bufferVal, { units, steps: 1 });
    if (typeof buffer === 'undefined') {
        throw new Error('Buffer failed');
    }
    // buffer may be Polygon | MultiPolygon - converting to MultiPolygon ensures consistency
    return toMultiPolygon(buffer.geometry).coordinates[0][0];
};
export const getPolygonCenter = (coordinates) => {
    const polygon = turf.polygon([coordinates]);
    const centerPoint = turf.centerOfMass(polygon);
    return centerPoint.geometry.coordinates.slice(0, 2);
};
export const getCenterCoordsStr = (coordinates, useDirectionalNotation) => {
    const center = getPolygonCenter(coordinates);
    // geojson is lng,lat (switch order to standard human readable)
    if (!useDirectionalNotation) {
        return `${roundToDecimals(center[1], 5)}, ${roundToDecimals(center[0], 5)}`;
    }
    else {
        return `${roundToDecimals(Math.abs(center[1]), 4)}°&nbsp;${center[1] > 0 ? 'N' : 'S'}, ${roundToDecimals(Math.abs(center[0]), 4)}°&nbsp;${center[0] > 0 ? 'E' : 'W'}`;
    }
};
export const getPolygonAroundCenter = (newCenterCoordinates, oldPolygonCoordinates) => {
    const oldPolygon = turf.polygon([oldPolygonCoordinates]);
    const oldCenter = turf.centroid(oldPolygon).geometry.coordinates;
    const from = turf.point([oldCenter[0], oldCenter[1]]);
    const to = turf.point(newCenterCoordinates);
    const bearing = turf.rhumbBearing(from, to);
    const distance = turf.rhumbDistance(from, to);
    return turf.transformTranslate(oldPolygon, distance, bearing).geometry
        .coordinates;
};
export const getExternalMask = (coordinates) => {
    const polygon = turf.polygon([coordinates]);
    return turf.mask(polygon);
};
export const pointWithinPolygon = (coordinates, polygonCoordinates) => {
    const polygon = turf.polygon([polygonCoordinates]);
    const point = turf.point(coordinates);
    return turf.booleanPointInPolygon(point, polygon);
};
/*
  Validations
*/
export const polygonIsValid = (coordinates) => {
    if (coordinates?.length) {
        const poly = turf.polygon([coordinates]);
        const intersections = turf.kinks(poly);
        return intersections.features.length === 0;
    }
    return false;
};
const getLargestFeatureCoords = (features) => {
    let maxArea = 0;
    let maxAreaIndex = -1;
    features.forEach((feature, i) => {
        const area = turf.area(feature);
        if (area > maxArea) {
            maxArea = area;
            maxAreaIndex = i;
        }
    });
    if (maxAreaIndex > -1) {
        const coordinates = features[maxAreaIndex].geometry.coordinates;
        if (coordinates?.length) {
            // Gets first set of coordinates in a Polygon
            return coordinates[0];
        }
    }
    return null;
};
export const forceSimplePolygon = (geometry, featureId) => {
    const multiPolygon = toMultiPolygon(geometry);
    let singlePolyCoords;
    if (multiPolygon.coordinates.length !== 1) {
        // flatten, get largest polygon from list and use to create site
        const flattenedFeatureCollection = turf.flatten(multiPolygon);
        const largestFeatureCoords = getLargestFeatureCoords(flattenedFeatureCollection.features);
        if (largestFeatureCoords) {
            singlePolyCoords = largestFeatureCoords;
        }
        else {
            throw Error(`geometry.coordinates must be of length 1. Feature id: ${featureId}. Received: ${JSON.stringify(geometry)}`);
        }
    }
    else if (multiPolygon.coordinates[0]?.[0]) {
        singlePolyCoords = multiPolygon.coordinates[0][0];
    }
    if (polygonIsValid(singlePolyCoords)) {
        return singlePolyCoords;
    }
    else if (singlePolyCoords) {
        const polygon = turf.polygon([singlePolyCoords]);
        // simplify (reduces the number of coordinates to a lower resolution)
        const simplifiedPoly = turf.simplify(polygon, {
            // tolerance correlates with decimal precision of coordinates:
            // 5 digits is the sweet spot - 6 digits can lead to errors in unkinkPolygon
            tolerance: 0.00001,
            mutate: true
        });
        // Unkink, get largest polygon from list and use to create site
        const unkinkedPolys = turf.unkinkPolygon(simplifiedPoly);
        const largestFeatureCoords = getLargestFeatureCoords(unkinkedPolys.features);
        if (largestFeatureCoords) {
            return largestFeatureCoords;
        }
    }
    // if we got this far, it's a lost cause
    throw Error(`Polygon is invalid. Feature id: ${featureId}. Received: ${JSON.stringify(geometry)}`);
};
//# sourceMappingURL=polygons.js.map